import { Button, DatePicker } from "@devhop/ui";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type PackageHistoryFilterProps = {
  filters: FilterConditionDTO[];
  onFiltersChange: (filters: FilterConditionDTO[] | undefined) => void;
};

export function PackageHistoryFilter({
  filters,
  onFiltersChange,
}: PackageHistoryFilterProps) {
  const statusFilter = findCondition(filters, "status");
  const purchasedAtFilter = findCondition(filters, "purchasedAt");
  const packageTypeFilter = findCondition(filters, "packageType");

  const [statusValue, setStatusValue] = useState<string>(
    (statusFilter?.value as string) || "all",
  );
  const [packageTypeValue, setPackageTypeValue] = useState<string>(
    (packageTypeFilter?.value as string) || "all",
  );
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    setStatusValue((statusFilter?.value as string) || "all");
  }, [statusFilter?.value]);

  useEffect(() => {
    setPackageTypeValue((packageTypeFilter?.value as string) || "all");
  }, [packageTypeFilter?.value]);

  const handleStatusChange = (val: string) => {
    setStatusValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val === "all") {
      nextFilters = removeConditions(filters, "status");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "status",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handlePackageTypeChange = (val: string) => {
    setPackageTypeValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val === "all") {
      nextFilters = removeConditions(filters, "packageType");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "packageType",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleDateRangeChange = (
    range:
      | {
          from?: Date;
          to?: Date;
        }
      | Date[]
      | undefined,
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;

    setFromDate(from);
    setToDate(to);

    const fromStr = from ? format(from, "yyyy-MM-dd") : "";
    const toStr = to ? format(to, "yyyy-MM-dd") : "";

    let nextFilters: FilterConditionDTO[] | undefined = filters;
    nextFilters = removeConditions(nextFilters, "purchasedAt");

    if (fromStr || toStr) {
      const dateRange: FilterConditionDTO[] = [];
      if (fromStr) {
        dateRange.push({
          field: "purchasedAt",
          op: "gte",
          value: `${fromStr} 00:00:00`,
        });
      }
      if (toStr) {
        dateRange.push({
          field: "purchasedAt",
          op: "lte",
          value: `${toStr} 23:59:59`,
        });
      }

      if (dateRange.length > 0) {
        nextFilters = upsertCondition(nextFilters, {
          field: "purchasedAt",
          op: "and",
          value: dateRange as unknown as FilterConditionDTO["value"],
        });
      }
    }

    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const hasFilter =
    !!statusFilter?.value ||
    !!purchasedAtFilter ||
    !!packageTypeFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <SimpleSelect
            value={packageTypeValue}
            onValueChange={handlePackageTypeChange}
            placeholder="ປະເພດແພັກເກດ"
            className="h-8 w-full sm:w-48"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "package", label: "ແພັກເກດ (ຄັ້ງ)" },
              { value: "private_lesson_package", label: "ແພັກເກດຮຽນສ່ວນຕົວ (ຊົ່ວໂມງ)" },
            ]}
          />

          <SimpleSelect
            value={statusValue}
            onValueChange={handleStatusChange}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "expired", label: "ໝົດອາຍຸ" },
              { value: "used_up", label: "ໃຊ້ຄົບ" },
              { value: "canceled", label: "ຍົກເລີກ" },
            ]}
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate,
              to: toDate,
            }}
            onChange={handleDateRangeChange}
            placeholder="ຊ່ວງວັນທີຊື້"
            className="w-full sm:w-fit"
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onFiltersChange(undefined);
              setToDate(undefined);
              setFromDate(undefined);
              setPackageTypeValue("all");
            }}
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
